import type { ActionKey } from "@acc/shared"

export type { ActionKey }

type IMessage = { type: string; data?: { key?: ActionKey } & unknown }

const RECONNECT_DELAY_MS = 3000
const MAX_RECONNECT_DELAY_MS = 30000

export class WSocketService {
  private static socket: WebSocket | null = null
  static isConnected: boolean | null = null
  private static listeners: Map<string, ((data: IMessage["data"]) => void)[]> =
    new Map()
  private static url: string = ""
  private static reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private static reconnectDelay: number = RECONNECT_DELAY_MS
  private static shouldReconnect: boolean = true

  static connect(url: string) {
    if (this.isConnected) return

    this.url = url
    this.shouldReconnect = true
    this.createConnection()
  }

  private static createConnection() {
    if (!this.url) return

    const socket = new WebSocket(this.url)
    this.socket = socket

    socket.onopen = () => {
      this.isConnected = true
      this.reconnectDelay = RECONNECT_DELAY_MS
    }

    socket.onmessage = (event: MessageEvent<string>) => {
      try {
        const message: IMessage = JSON.parse(event.data)
        const { data, type } = message
        const listeners = this.listeners.get(type) || []
        listeners.forEach((listener) => listener(data))
      } catch (e) {
        console.error(e)
      }
    }

    socket.onclose = () => {
      this.isConnected = false
      this.socket = null
      this.scheduleReconnect()
    }

    socket.onerror = () => {
      socket.close()
    }
  }

  private static scheduleReconnect() {
    if (!this.shouldReconnect) return
    if (this.reconnectTimer) return

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (this.shouldReconnect && !this.isConnected) {
        this.createConnection()
      }
    }, this.reconnectDelay)

    this.reconnectDelay = Math.min(
      this.reconnectDelay * 1.5,
      MAX_RECONNECT_DELAY_MS,
    )
  }

  static subscribe(type: string, listener: (data: IMessage["data"]) => void) {
    const listeners = this.listeners.get(type) || []
    listeners.push(listener)
    this.listeners.set(type, listeners)
  }

  static unsubscribe(type: string, listener: (data: IMessage["data"]) => void) {
    const listeners = this.listeners.get(type) || []
    const filtered = listeners.filter((l) => l !== listener)
    if (filtered.length) {
      this.listeners.set(type, filtered)
    } else {
      this.listeners.delete(type)
    }
  }

  static disconnect() {
    this.shouldReconnect = false
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.socket) {
      this.socket.close()
      this.socket = null
      this.isConnected = false
    }
    this.listeners.clear()
  }
}
